import { createModule } from "saga-slice";
import { call, put } from "redux-saga/effects";
import lotService from "../services/lotService";

const initialState = {
    lots: [],
    selectedLot: null,
    mouvements: [],
    statistics: null,
    lotsExpirants: [],
    loading: false,
    loadingMouvements: false,
    error: null,
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: 10,
};

const lotSlice = createModule({
    name: 'lots',
    initialState,
    reducers: {
        // Actions pour la liste des lots
        listRequest: (state) => {
            state.loading = true;
            state.error = null;
        },
        listSuccess: (state, { payload }) => {
            state.loading = false;
            state.lots = payload.content || payload;
            state.totalElements = payload.totalElements || 0;
            state.totalPages = payload.totalPages || 0;
            state.currentPage = payload.number || 0;
        },
        listFailure: (state, { payload }) => {
            state.loading = false;
            state.error = payload;
        },

        // Actions pour créer un lot
        createRequest: (state) => {
            state.loading = true;
            state.error = null;
        },
        createSuccess: (state, { payload }) => {
            state.loading = false;
            state.lots.unshift(payload);
        },
        createFailure: (state, { payload }) => {
            state.loading = false;
            state.error = payload;
        },

        // Actions pour mettre à jour un lot
        updateRequest: (state) => {
            state.loading = true;
            state.error = null;
        },
        updateSuccess: (state, { payload }) => {
            state.loading = false;
            const index = state.lots.findIndex(lot => lot.numeroLot === payload.numeroLot);
            if (index !== -1) {
                state.lots[index] = payload;
            }
            if (state.selectedLot?.numeroLot === payload.numeroLot) {
                state.selectedLot = payload;
            }
        },
        updateFailure: (state, { payload }) => {
            state.loading = false;
            state.error = payload;
        },

        // Actions pour supprimer un lot
        deleteRequest: (state) => {
            state.loading = true;
            state.error = null;
        },
        deleteSuccess: (state, { payload }) => {
            state.loading = false;
            state.lots = state.lots.filter(lot => lot.numeroLot !== payload.numeroLot);
        },
        deleteFailure: (state, { payload }) => {
            state.loading = false;
            state.error = payload;
        },

        // Actions pour changer le statut
        changeStatutRequest: (state) => {
            state.loading = true;
            state.error = null;
        },
        changeStatutSuccess: (state, { payload }) => {
            state.loading = false;
            const index = state.lots.findIndex(lot => lot.numeroLot === payload.numeroLot);
            if (index !== -1) {
                state.lots[index].statut = payload.statut;
            }
        },
        changeStatutFailure: (state, { payload }) => {
            state.loading = false;
            state.error = payload;
        },

        // Actions pour générer un numéro de lot
        generateNumeroLotRequest: (state) => {
            state.loading = true;
        },
        generateNumeroLotSuccess: (state) => {
            state.loading = false;
        },
        generateNumeroLotFailure: (state, { payload }) => {
            state.loading = false;
            state.error = payload;
        },

        // Actions pour les mouvements de stock
        createMouvementRequest: (state) => {
            state.loading = true;
            state.error = null;
        },
        createMouvementSuccess: (state, { payload }) => {
            state.loading = false;
            // Mettre à jour la quantité disponible du lot
            const index = state.lots.findIndex(lot => lot.numeroLot === payload.numeroLot);
            if (index !== -1) {
                state.lots[index].quantiteDisponible = payload.nouvelleQuantite;
            }
        },
        createMouvementFailure: (state, { payload }) => {
            state.loading = false;
            state.error = payload;
        },

        // Actions pour l'historique des mouvements
        getHistoriqueMouvementsRequest: (state) => {
            state.loadingMouvements = true;
            state.error = null;
        },
        getHistoriqueMouvementsSuccess: (state, { payload }) => {
            state.loadingMouvements = false;
            state.mouvements = payload;
        },
        getHistoriqueMouvementsFailure: (state, { payload }) => {
            state.loadingMouvements = false;
            state.error = payload;
        },

        // Actions pour les statistiques
        getStatisticsRequest: (state) => {
            state.loading = true;
        },
        getStatisticsSuccess: (state, { payload }) => {
            state.loading = false;
            state.statistics = payload;
        },
        getStatisticsFailure: (state, { payload }) => {
            state.loading = false;
            state.error = payload;
        },

        // Nouvelles actions pour les lots qui expirent
        getExpiringLotsRequest: (state, action) => {
            state.loading = true;
            state.error = null;
        },
        getExpiringLotsSuccess: (state, action) => {
            state.loading = false;
            state.lotsExpirants = action.payload.content || action.payload;
            // Optionnel: mettre à jour les métadonnées de pagination si nécessaire
            if (action.payload.totalPages !== undefined) {
                state.totalPages = action.payload.totalPages;
                state.totalElements = action.payload.totalElements;
                state.currentPage = action.payload.number || 0;
            }
        },
        getExpiringLotsFailure: (state, action) => {
            state.loading = false;
            state.error = action.payload;
            state.lotsExpirants = [];
        },

        // Action pour nettoyer les lots qui expirent
        clearExpiringLots: (state) => {
            state.lotsExpirants = [];
        },

        // Action pour sélectionner un lot
        selectLot: (state, { payload }) => {
            state.selectedLot = payload;
        },

        // Action pour nettoyer les erreurs
        clearError: (state) => {
            state.error = null;
        },

        // Action pour réinitialiser l'état
        reset: (state) => {
            return initialState;
        }
    },

    sagas: (A) => ({
        // Saga pour lister les lots
        *[A.listRequest]({ payload }) {
            try {
                const response = yield call(lotService.list, payload);
                yield put(A.listSuccess(response));
            } catch (error) {
                yield put(A.listFailure(error.message || 'Erreur lors du chargement des lots'));
            }
        },

        // Saga pour créer un lot
        *[A.createRequest]({ payload }) {
            try {
                const response = yield call(lotService.create, payload.data);
                yield put(A.createSuccess(response));
                if (payload.onSuccess) {
                    payload.onSuccess(response);
                }
            } catch (error) {
                yield put(A.createFailure(error.message || 'Erreur lors de la création du lot'));
                if (payload.onError) {
                    payload.onError(error);
                }
            }
        },

        // Saga pour mettre à jour un lot
        *[A.updateRequest]({ payload }) {
            try {
                const response = yield call(lotService.update, payload.numeroLot, payload.data);
                yield put(A.updateSuccess(response));
                if (payload.onSuccess) {
                    payload.onSuccess(response);
                }
            } catch (error) {
                yield put(A.updateFailure(error.message || 'Erreur lors de la mise à jour du lot'));
                if (payload.onError) {
                    payload.onError(error);
                }
            }
        },

        // Nouvelle saga pour les lots qui expirent
        *[A.getExpiringLotsRequest]({ payload }) {
            try {
                const response = yield call(lotService.getLotsExpiration, payload);
                yield put(A.getExpiringLotsSuccess(response));
                
                if (payload?.onSuccess) {
                    payload.onSuccess(response);
                }
            } catch (error) {
                const errorMessage = error.message || 'Erreur lors du chargement des lots qui expirent';
                yield put(A.getExpiringLotsFailure(errorMessage));
                
                if (payload?.onError) {
                    payload.onError(error);
                }
            }
        },

        // Saga pour supprimer un lot
        *[A.deleteRequest]({ payload }) {
            try {
                yield call(lotService.delete, payload.numeroLot);
                yield put(A.deleteSuccess({ numeroLot: payload.numeroLot }));
                if (payload.onSuccess) {
                    payload.onSuccess();
                }
            } catch (error) {
                yield put(A.deleteFailure(error.message || 'Erreur lors de la suppression du lot'));
                if (payload.onError) {
                    payload.onError(error);
                }
            }
        },

        // Saga pour changer le statut
        *[A.changeStatutRequest]({ payload }) {
            try {
                yield call(lotService.changeStatut, payload.numeroLot, payload.statut);
                yield put(A.changeStatutSuccess({
                    numeroLot: payload.numeroLot,
                    statut: payload.statut
                }));
                if (payload.onSuccess) {
                    payload.onSuccess();
                }
            } catch (error) {
                yield put(A.changeStatutFailure(error.message || 'Erreur lors du changement de statut'));
                if (payload.onError) {
                    payload.onError(error);
                }
            }
        },

        // Saga pour générer un numéro de lot
        *[A.generateNumeroLotRequest]({ payload }) {
            try {
                const response = yield call(lotService.generateNumeroLot, payload);
                yield put(A.generateNumeroLotSuccess());
                return { payload: { body: response.numeroLot } };
            } catch (error) {
                yield put(A.generateNumeroLotFailure(error.message || 'Erreur lors de la génération du numéro'));
                throw error;
            }
        },

        // Saga pour créer un mouvement
        *[A.createMouvementRequest]({ payload }) {
            try {
                const response = yield call(lotService.createMouvement, payload.numeroLot, payload.data);
                yield put(A.createMouvementSuccess(response));
                if (payload.onSuccess) {
                    payload.onSuccess(response);
                }
            } catch (error) {
                yield put(A.createMouvementFailure(error.message || 'Erreur lors de la création du mouvement'));
                if (payload.onError) {
                    payload.onError(error);
                }
            }
        },

        // Saga pour l'historique des mouvements
        *[A.getHistoriqueMouvementsRequest]({ payload }) {
            try {
                const response = yield call(lotService.getHistoriqueMouvements, payload.numeroLot);
                yield put(A.getHistoriqueMouvementsSuccess(response));
            } catch (error) {
                yield put(A.getHistoriqueMouvementsFailure(error.message || 'Erreur lors du chargement de l\'historique'));
            }
        },

        // Saga pour les statistiques
        *[A.getStatisticsRequest]({ payload }) {
            try {
                const response = yield call(lotService.getStatistics);
                yield put(A.getStatisticsSuccess(response));
            } catch (error) {
                yield put(A.getStatisticsFailure(error.message || 'Erreur lors du chargement des statistiques'));
            }
        }
    }),
});

export const lotActions = lotSlice.actions;
export default lotSlice;
